import { type AxiosError } from "axios";

import { captureEvent } from "@vega-core/utils/posthog/captureEvent";
import { sanitiseEndpoint } from "@vega-core/utils/posthog/sanitiseEndpoint";

const TRACE_HEADER_KEYS = ["x-trace-id", "x-amzn-trace-id", "traceparent"];

// 401/403 are handled by errorInterceptor (token refresh / session-expiry
// logout) and would otherwise fire once per in-flight request during a
// session-expiry storm.
const SKIPPED_STATUS_CODES = new Set([401, 403]);

const getTraceId = (
  headers: Record<string, unknown> | undefined,
): string | undefined => {
  if (!headers) return undefined;

  for (const key of TRACE_HEADER_KEYS) {
    const value = headers[key];

    if (typeof value === "string" && value.length > 0) return value;
  }

  return undefined;
};

export const apiErrorInterceptor = (error: AxiosError) => {
  const statusCode = error.response?.status;

  if (
    typeof statusCode !== "number" ||
    statusCode < 400 ||
    SKIPPED_STATUS_CODES.has(statusCode)
  ) {
    return Promise.reject(error);
  }

  captureEvent("api_error", {
    status_code: statusCode,
    endpoint: sanitiseEndpoint(error.config?.url, error.config?.baseURL),
    method: error.config?.method?.toUpperCase(),
    trace_id: getTraceId(
      error.response?.headers as Record<string, unknown> | undefined,
    ),
  });

  return Promise.reject(error);
};
